import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import { useAuth } from "../../../../context/AuthContext";
import StoreCategoriesTable from "./StoreCategoriesTable";
import StoreCategoryDeleteModal from "./StoreCategoryDeleteModal";
import StoreCategoryFormModal from "./StoreCategoryFormModal";

export default function StoreCategoriesPanel() {
  const { authFetch, user } = useAuth();

  const [categories, setCategories] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [actionLoadingId, setActionLoadingId] = useState("");

  const [formOpen, setFormOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);

  const [banner, setBanner] = useState({
    type: "",
    message: "",
  });

  const canManage = user?.role === "admin";

  const loadData = async () => {
    setLoading(true);
    setBanner({ type: "", message: "" });

    try {
      const response = await authFetch("/api/store/categories/");

      const data = await response.json();

      if (!response.ok) {
        setBanner({
          type: "error",
          message: "No se pudieron cargar las categorías",
        });
        return;
      }

      setCategories(data);
    } catch {
      setBanner({
        type: "error",
        message: "Error cargando categorías",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const filtered = useMemo(() => {
    const term = query.toLowerCase().trim();

    if (!term) return categories;

    return categories.filter(
      (c) =>
        c.name.toLowerCase().includes(term) ||
        c.code.toLowerCase().includes(term) ||
        c.section_name.toLowerCase().includes(term)
    );
  }, [categories, query]);

  const handleCreate = () => {
    setSelectedCategory(null);
    setFormOpen(true);
  };

  const handleEdit = (category) => {
    setSelectedCategory(category);
    setFormOpen(true);
  };

  const handleDeleteOpen = (category) => {
    setSelectedCategory(category);
    setDeleteOpen(true);
  };

  const handleDeleteClose = () => {
    setDeleteOpen(false);
    setSelectedCategory(null);
  };

  const handleDelete = async () => {
    if (!selectedCategory) return;

    setActionLoadingId(selectedCategory.uuid);

    const response = await authFetch(
      `/api/store/categories/${selectedCategory.uuid}/delete/`,
      { method: "DELETE" }
    );

    if (response.ok) {
      setCategories((prev) =>
        prev.filter((c) => c.uuid !== selectedCategory.uuid)
      );
    }

    setDeleteOpen(false);
    setActionLoadingId("");
  };

  return (
    <Box width="100%">
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        spacing={1.4}
        mb={1.8}
      >
        <Stack direction="row" spacing={1}>
          <TextField
            size="small"
            placeholder="Buscar categoría"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <SearchRoundedIcon
                  sx={{ fontSize: 18, color: "#64748b", mr: 0.8 }}
                />
              ),
            }}
            sx={{
              minWidth: { xs: "100%", sm: 400 },
              "& .MuiOutlinedInput-root": {
                height: 38,
                borderRadius: "10px",
                bgcolor: "#fff",
              },
            }}
          />

          <Button
            onClick={loadData}
            variant="outlined"
            startIcon={<RefreshRoundedIcon />}
          >
            Recargar
          </Button>
        </Stack>

        {canManage && (
          <Button
            onClick={handleCreate}
            variant="contained"
            startIcon={<AddRoundedIcon />}
          >
            Nueva categoría
          </Button>
        )}
      </Stack>

      {loading ? (
        <CircularProgress size={26} />
      ) : (
        <StoreCategoriesTable
          categories={filtered}
          onEdit={handleEdit}
          onDelete={handleDeleteOpen}
          canManage={canManage}
          actionLoadingId={actionLoadingId}
        />
      )}

      <StoreCategoryDeleteModal
        open={deleteOpen}
        onClose={handleDeleteClose}
        onConfirm={handleDelete}
        category={selectedCategory}
        loading={false}
      />

      <StoreCategoryFormModal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        category={selectedCategory}
        reload={loadData}
      />
    </Box>
  );
}